<script lang="ts">
	import type { HomepageProps } from "./HomepageProps";
	import Layout from "../components/Layout.svelte";

	import imgPost from "../../../core/assets/images/post.png?webp&h=201&imagetools";
	import imgStudio from "../../../core/assets/images/studio.png?webp&w=588&imagetools";
	import imgStudioChat from "../../../core/assets/images/studio-chat.png?webp&w=596&imagetools";
	import imgStudioDetail from "../../../core/assets/images/studio-chat-detail.png?webp&w=465&imagetools";
	import imgPlayer from "../../../core/assets/images/player.png?webp&w=474&imagetools";
	import imgGarage from "../../../core/assets/images/garage.png?webp&w=596&imagetools";
	import imgRocksPattern from "../../../core/assets/images/rocks-pattern.png?webp&w=2871&imagetools";
	import imgNotesPattern from "../../../core/assets/images/notes-pattern.png?webp&w=1864&imagetools";
	import logoMusicCity from "../../../core/assets/images/music-city.png?webp&w=234&imagetools";
	import logoBandzone from "../../../core/assets/images/bandzone.png?webp&w=307&imagetools";
	import logoAkustickaPena from "../../../core/assets/images/akusticka-pena.png?webp&w=331&imagetools";
	import wavesimage from "../../../core/assets/images/waves.png?webp&w=770&imagetools";

	import Hero from "../components/Hero.svelte";
	import NoBand from "../components/NoBand.svelte";
	import Benefits from "../components/Benefits.svelte";
	import KeepAnEye from "../components/KeepAnEye.svelte";
	import Counter from "../components/Counter.svelte";
	import ForWho from "../components/ForWho.svelte";
	import Video from "../components/Video.svelte";
	import Link from "../components/Link.svelte";
	import StaticPicture from "../components/Picture/StaticPicture.svelte";
	import Feature from "../components/Feature.svelte";
	import Reviews from "../components/Reviews.svelte";
	import imgRocksPatternFooter from "../../../core/assets/images/rocks-pattern-footer.png?webp&w=2817&imagetools";
	import { Locale } from "../../../core/schema/Locale";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./Homepage.translations.json";
	import Pricing from "../components/Pricing.svelte";
	import { getTenant } from "../contexts/tenantContextKey";
	import { getSignUpUrl } from "../utils/getSignUpUrl";
	import { getGlobalSettings } from "../getGlobalSettings";

	type $$Props = HomepageProps;

	const { WEB_URL } = getGlobalSettings();

	const translate = getTranslate(translations);
	const { locale } = getTenant();
	const signUpUrl = getSignUpUrl(WEB_URL);

	/* eslint-disable svelte/no-at-html-tags */
</script>

<Layout>
	<Hero
		headline={translate("headline")}
		description={translate("description")}
		tryApp={translate("tryApp")}
		login={translate("login")}
		url={signUpUrl}
	/>

	<div
		class="animate-pulse bg-[center_bottom_2rem] bg-repeat-x pt-36 lg:pt-96"
		style:background-image="url({wavesimage})"
	></div>
	<Video musicEasier1={translate("musicEasier1")} musicEasier2={translate("musicEasier2")} />

	<div class="bg-primary hidden py-48 lg:block"></div>

	<section class="flex flex-col bg-white lg:container lg:gap-48">
		<div class="z-10 lg:-mt-[32rem]">
			<Reviews href={signUpUrl} reviewsHeadline={translate("reviewsHeadline")} tryApp={translate("tryApp")} />
		</div>
	</section>

	<section class="flex flex-col bg-white lg:container lg:gap-48">
		<div class="container flex flex-col gap-32 py-32 lg:gap-64">
			<Feature
				headline={translate("featureHeadline1")}
				description={translate("featureDescription1")}
				tryApp={translate("tryApp")}
				href={signUpUrl}
				imgUrl={imgStudio}
			></Feature>
			<Feature
				headline={translate("featureHeadline2")}
				description={translate("featureDescription2")}
				tryApp={translate("tryApp")}
				href={signUpUrl}
				imgUrl={imgStudioChat}
				reverseOrder
			></Feature>
			<Feature
				headline={translate("featureHeadline3")}
				description={translate("featureDescription3")}
				tryApp={translate("tryApp")}
				href={signUpUrl}
				imgUrl={imgStudioDetail}
			></Feature>
			<Feature
				headline={translate("featureHeadline4")}
				description={translate("featureDescription4")}
				tryApp={translate("tryApp")}
				href={signUpUrl}
				imgUrl={imgPlayer}
				reverseOrder
			></Feature>
			<Feature
				headline={translate("featureHeadline5")}
				description={translate("featureDescription5")}
				tryApp={translate("tryApp")}
				href={signUpUrl}
				imgUrl={imgGarage}
			></Feature>
		</div>
	</section>

	<section class="bg-secondary text-white">
		<div class="container">
			<NoBand noBandOk={translate("noBandOk")} {imgPost} noBandDescription={translate("noBandDescription")} />
			<Benefits href={signUpUrl} takeTheAdvantageHeadline={translate("takeTheAdvantageHeadline")} />
		</div>
	</section>

	<KeepAnEye
		keepAnEyeOnThingsHeadline={translate("keepAnEyeOnThingsHeadline")}
		keepAnEyeOnThingsDescription={translate("keepAnEyeOnThingsDescription")}
	/>

	<section class="bg-secondary relative text-white">
		<div class="hidden bg-white py-40 lg:block"></div>
		<StaticPicture
			imgClass="absolute -top-48 z-0"
			width={2871}
			height={1000}
			image={imgRocksPattern}
			alt="Rocks pattern"
		/>

		<div class="relative z-10 lg:container">
			<Counter
				enjoyJamujamHeadline={translate("enjoyJamujamHeadline")}
				enjoyCol1Description={translate("enjoyCol1Description")}
				enjoyCol2Description={translate("enjoyCol2Description")}
				enjoyCol3Description={translate("enjoyCol3Description")}
				tryApp={translate("tryApp")}
				href={signUpUrl}
			/>
			<ForWho
				forWhoHeadline={translate("forWhoHeadline")}
				forWhoContent1={translate("forWhoContent1")}
				forWhoContent2={translate("forWhoContent2")}
				forWhoContent3={translate("forWhoContent3")}
				tryApp={translate("tryApp")}
				href={signUpUrl}
			/>
		</div>
	</section>

	<section class="bg-secondary-100 relative -scroll-mt-96 py-16 lg:-scroll-mt-52 lg:py-32">
		<div class="container relative z-10">
			<h2 class="text-md max-w-[65rem] text-center lg:mb-12 lg:text-lg">{@html translate("pricingHeadline")}</h2>
			<h2 id="pricing" class="text-md my-14 max-w-[65rem] text-center lg:mb-12 lg:text-lg">
				{@html translate("choosePlan")}
			</h2>
			<StaticPicture
				imgClass="absolute w-[1864px] h-[799px] object-contain -top-24 rotate-180 opacity-50 -left-24"
				width={1864}
				height={799}
				image={imgNotesPattern}
				alt="Notes pattern"
			/>
			<StaticPicture
				imgClass="absolute -top-24 w-[1864px] h-[799px] object-contain z-0 opacity-50 -right-24"
				width={1864}
				height={799}
				image={imgNotesPattern}
				alt="Notes pattern"
			/>
			<div class="flex flex-col items-center justify-center gap-4 lg:gap-8 xl:flex-row xl:items-center">
				<Pricing />
			</div>
		</div>
	</section>

	<section class="py-16 lg:py-32 lg:pb-64">
		<div class="container">
			<h2 class="text-md text-center lg:text-lg">
				{@html translate("jamujamInMediaHeadline")}
			</h2>
			<div class="mt-16 flex flex-col items-center justify-center gap-2 lg:mt-4 lg:flex-row lg:gap-16">
				<StaticPicture width={307} height={77} image={logoBandzone} alt="Bandzone" />
				<StaticPicture width={234} height={132} image={logoMusicCity} alt="Music City" />
				<StaticPicture width={331} height={68} image={logoAkustickaPena} alt="Akusticka Pena" />
			</div>
			<Link variant="primary" asButton href={signUpUrl} class="mx-auto mt-12">
				{@html translate("tryApp")}
			</Link>
		</div>
	</section>

	<div class="bg-secondary relative py-32 text-white lg:py-0">
		<div
			class="container relative z-10 mx-auto aspect-video max-w-[90%] lg:-top-[100px] lg:-mb-[100px] lg:max-w-[1029px]"
		>
			<div class="bg-primary border-primary absolute -inset-1 -z-10 rounded-lg"></div>
			<div class="absolute inset-0 overflow-clip rounded-md">
				<iframe
					title="Jamujam welcome video"
					class="absolute inset-0 h-full w-full"
					src={locale === Locale.cs
						? "https://player.vimeo.com/video/1047063663?badge=0&autopause=0&player_id=0&app_id=58479"
						: "https://player.vimeo.com/video/658333889?badge=0&autopause=0&player_id=0&app_id=58479"}
					width="1029"
					height="578"
					frameborder="0"
					allow="autoplay; fullscreen; picture-in-picture"
					allowfullscreen={true}
				></iframe>
			</div>
		</div>

		<StaticPicture
			imgClass="absolute z-0 top-0 h-full object-cover lg:object-cover"
			width={2871}
			height={1000}
			image={imgRocksPatternFooter}
			alt="Rocks pattern"
		/>
	</div>
</Layout>
